import { useMemo, useState } from "react";
import Swal from "sweetalert2";
import Modal from "../../../components/base/Modal";
import { Badge, DIALOG_COLORS, EmptyState, cn } from "../../../components/ui";
import EntityForm from "../components/EntityForm";
import type { FormField } from "../components/ApiCrudSection";
import { createWorkshop, deleteWorkshop, updateWorkshop } from "../services/maintenanceService";
import { buildThemeClasses, MODE_TONES, WORKSHOP_MODES } from "../theme";
import { useMxLabels } from "../hooks/useMxLabels";
import { useWorkshops } from "../hooks/useWorkshops";
import { useMaintenanceRecords } from "../hooks/useMaintenanceRecords";
import { useMaintenanceCapabilities } from "../hooks/useMaintenanceCapabilities";
import { costOf, fmtMoney, isOpen } from "../lib";
import type { Workshop, WorkshopMode } from "../types";
import { KpiCard, LoadingBlock, PageShell, SearchInput, SelectFilter, ToolbarButton, ViewSwitch } from "../components/kit/Primitives";
import { Photo } from "../components/kit/Media";

type View = "cards" | "table";

// Talleres y mecánicos: tarjetas con foto, modo (propio = sin costo, externo =
// cobra), ciudad, bahías, especialidades y su carga real (órdenes abiertas y
// gasto acumulado). El modo sigue siendo la fuente de verdad del cobro.
export default function TalleresSection() {
  const L = useMxLabels();
  const { t } = L;
  const tc = buildThemeClasses();
  const { capabilities } = useMaintenanceCapabilities();
  const { workshops, loading, reload } = useWorkshops();
  const { rows } = useMaintenanceRecords();
  const [view, setView] = useState<View>("cards");
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("");
  const [form, setForm] = useState<null | { workshop?: Workshop | null }>(null);

  const stats = useMemo(() => {
    const out: Record<string, { orders: number; open: number; spend: number }> = {};
    for (const r of rows) {
      const k = r.workshopId || "";
      if (!k) continue;
      const cur = out[k] || (out[k] = { orders: 0, open: 0, spend: 0 });
      cur.orders += 1;
      if (isOpen(r)) cur.open += 1;
      if (r.status === "completado") cur.spend += costOf(r);
    }
    return out;
  }, [rows]);

  const visible = workshops
    .filter((w) => (!mode || w.mode === mode) && [w.name, w.city, w.address, ...(w.specialties || [])].filter(Boolean).some((x) => String(x).toLowerCase().includes(query.trim().toLowerCase())))
    .sort((a, b) => Number(b.active !== false) - Number(a.active !== false) || (stats[b.id]?.orders || 0) - (stats[a.id]?.orders || 0));

  const fields: FormField[] = useMemo(
    () => [
      { key: "name", label: t("mantenimiento.fields.workshopName"), type: "text", required: true },
      { key: "mode", label: t("mantenimiento.fields.workshopMode"), type: "select", required: true, defaultValue: "propio", options: WORKSHOP_MODES.map((v) => ({ value: v, label: L.mode(v) })) },
      { key: "city", label: t("mantenimiento.fieldsExtra.city"), type: "text" },
      { key: "bays", label: t("mantenimiento.fieldsExtra.bays"), type: "number" },
      { key: "contactName", label: t("mantenimiento.fields.contactName"), type: "text" },
      { key: "phone", label: t("mantenimiento.fields.phone"), type: "text" },
      { key: "address", label: t("mantenimiento.fields.address"), type: "text" },
      { key: "photo", label: t("mantenimiento.fieldsExtra.photo"), type: "text", placeholder: "https://…" },
      { key: "specialties", label: t("mantenimiento.ui.talleres.specialties"), type: "text", placeholder: "Frenos, Llantas…" },
      { key: "notes", label: t("mantenimiento.fields.notes"), type: "textarea" },
    ],
    [t, L]
  );

  const submit = async (values: Record<string, string>) => {
    const payload: Partial<Workshop> = {
      name: values.name,
      mode: (values.mode as WorkshopMode) || "propio",
      city: values.city || null,
      bays: values.bays ? Number(values.bays) : null,
      contactName: values.contactName || null,
      phone: values.phone || null,
      address: values.address || null,
      photo: values.photo || null,
      specialties: (values.specialties || "").split(",").map((s) => s.trim()).filter(Boolean),
      notes: values.notes || null,
    };
    if (form?.workshop) await updateWorkshop(form.workshop.id, payload);
    else await createWorkshop(payload);
    setForm(null);
    await reload();
  };

  const remove = async (w: Workshop) => {
    const res = await Swal.fire({
      title: t("mantenimiento.confirmDeleteTitle"),
      text: t("mantenimiento.confirmDeleteText"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("mantenimiento.delete"),
      cancelButtonText: t("mantenimiento.cancel"),
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    await deleteWorkshop(w.id);
    await reload();
  };

  const totals = {
    all: workshops.length,
    own: workshops.filter((w) => w.mode === "propio").length,
    ext: workshops.filter((w) => w.mode === "externo").length,
    bays: workshops.reduce((s, w) => s + (Number(w.bays) || 0), 0),
  };

  const Actions = ({ w }: { w: Workshop }) =>
    capabilities.canManageRecords ? (
      <div className="flex items-center gap-1">
        <button type="button" title={t("nuvexAccess.edit")} onClick={() => setForm({ workshop: w })} className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"><i className="ri-edit-line" /></button>
        <button type="button" title={t("mantenimiento.delete")} onClick={() => remove(w)} className="rounded-md p-1.5 text-error hover:bg-error/10"><i className="ri-delete-bin-line" /></button>
      </div>
    ) : null;

  return (
    <PageShell
      icon="ri-store-2-line"
      title={t("mantenimiento.menu.talleres")}
      subtitle={t("mantenimiento.talleresSubtitle")}
      actions={
        <>
          <ToolbarButton icon="ri-refresh-line" label={t("mantenimiento.ui.refresh")} onClick={reload} spinning={loading} />
          {capabilities.canManageRecords ? <ToolbarButton primary icon="ri-add-line" label={t("mantenimiento.newWorkshop")} onClick={() => setForm({ workshop: null })} /> : null}
        </>
      }
      toolbar={
        <>
          <SearchInput value={query} onChange={setQuery} placeholder={t("nuvexAccess.search")} />
          <SelectFilter value={mode} onChange={setMode} label={t("mantenimiento.fields.workshopMode")} options={[{ value: "", label: t("mantenimiento.ui.allModes") }, ...WORKSHOP_MODES.map((m) => ({ value: m, label: L.mode(m) }))]} />
          <span className="ml-auto text-xs text-content-muted">{t("mantenimiento.ui.results", { count: visible.length })}</span>
          <ViewSwitch value={view} onChange={setView} options={[{ id: "cards", icon: "ri-layout-grid-line", label: t("mantenimiento.ui.views.cards") }, { id: "table", icon: "ri-table-line", label: t("mantenimiento.ui.views.table") }]} />
        </>
      }
    >
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard icon="ri-store-2-line" tone="primary" label={t("mantenimiento.ui.talleres.kpiTotal")} value={totals.all} loading={loading} />
        <KpiCard icon="ri-home-gear-line" tone="info" label={t("mantenimiento.ui.talleres.kpiOwn")} value={totals.own} loading={loading} onClick={() => setMode("propio")} />
        <KpiCard icon="ri-store-3-line" tone="warning" label={t("mantenimiento.ui.talleres.kpiExternal")} value={totals.ext} loading={loading} onClick={() => setMode("externo")} />
        <KpiCard icon="ri-tools-line" tone="success" label={t("mantenimiento.ui.talleres.kpiBays")} value={totals.bays} loading={loading} />
      </div>

      {loading && !workshops.length ? (
        <LoadingBlock />
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line bg-card">
          <EmptyState icon="ri-store-2-line" title={t("nuvexAccess.empty")} />
        </div>
      ) : view === "cards" ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {visible.map((w) => {
            const s = stats[w.id] || { orders: 0, open: 0, spend: 0 };
            const m = w.mode === "externo" ? "externo" : "propio";
            return (
              <article key={w.id} className={cn("flex flex-col overflow-hidden rounded-xl border border-line bg-card", w.active === false ? "opacity-70" : "")}>
                <div className="relative h-36">
                  <Photo src={w.photo} alt={w.name} thumb fallbackIcon="ri-store-2-line" className="h-full w-full" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
                  <div className="absolute left-2 top-2 flex gap-1">
                    <Badge tone={MODE_TONES[m]} variant="solid" size="sm">{L.mode(m)}</Badge>
                    {w.active === false ? <Badge tone="neutral" variant="solid" size="sm">{t("mantenimiento.ui.talleres.inactive")}</Badge> : null}
                  </div>
                  <div className="absolute bottom-2 left-3 right-3">
                    <p className="truncate text-sm font-semibold text-white">{w.name}</p>
                    {w.city ? <p className="text-[11px] text-white/80"><i className="ri-map-pin-line mr-0.5" />{w.city}</p> : null}
                  </div>
                </div>
                <div className="flex flex-1 flex-col gap-3 p-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-subtle/60 py-1.5">
                      <p className="text-sm font-bold tabular-nums text-content">{s.orders}</p>
                      <p className="text-[10px] text-content-muted">{t("mantenimiento.ui.charts.orders")}</p>
                    </div>
                    <div className="rounded-lg bg-subtle/60 py-1.5">
                      <p className={cn("text-sm font-bold tabular-nums", s.open ? "text-warning" : "text-content")}>{s.open}</p>
                      <p className="text-[10px] text-content-muted">{t("mantenimiento.ui.kpi.openOrders")}</p>
                    </div>
                    <div className="rounded-lg bg-subtle/60 py-1.5">
                      <p className="text-sm font-bold tabular-nums text-content">{w.bays ?? "—"}</p>
                      <p className="text-[10px] text-content-muted">{t("mantenimiento.fieldsExtra.bays")}</p>
                    </div>
                  </div>
                  {w.specialties?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {w.specialties.map((sp) => <Badge key={sp} tone="neutral" variant="outline" size="sm">{sp}</Badge>)}
                    </div>
                  ) : null}
                  <div className="space-y-1 text-xs text-content-secondary">
                    {w.contactName ? <p className="truncate"><i className="ri-user-3-line mr-1" />{w.contactName}</p> : null}
                    {w.phone ? <p className="truncate"><i className="ri-phone-line mr-1" />{w.phone}</p> : null}
                    {w.address ? <p className="line-clamp-2"><i className="ri-map-pin-2-line mr-1" />{w.address}</p> : null}
                  </div>
                  <div className="mt-auto flex items-center justify-between border-t border-line pt-2">
                    <span className="text-xs text-content-secondary">
                      {t("mantenimiento.ui.talleres.spend")}: <b className="tabular-nums text-content">{m === "propio" && !s.spend ? t("mantenimiento.free") : fmtMoney(s.spend, L.locale)}</b>
                    </span>
                    <Actions w={w} />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-card">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-subtle/60 text-[11px] uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-3 py-2.5 text-left">{t("mantenimiento.fields.workshopName")}</th>
                <th className="px-3 py-2.5 text-left">{t("mantenimiento.fields.workshopMode")}</th>
                <th className="px-3 py-2.5 text-left">{t("mantenimiento.fieldsExtra.city")}</th>
                <th className="px-3 py-2.5 text-left">{t("mantenimiento.fields.contactName")}</th>
                <th className="px-3 py-2.5 text-right">{t("mantenimiento.ui.charts.orders")}</th>
                <th className="px-3 py-2.5 text-right">{t("mantenimiento.ui.talleres.spend")}</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {visible.map((w) => {
                const s = stats[w.id] || { orders: 0, open: 0, spend: 0 };
                const m = w.mode === "externo" ? "externo" : "propio";
                return (
                  <tr key={w.id} className="hover:bg-subtle/40">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2.5">
                        <Photo src={w.photo} alt={w.name} thumb fallbackIcon="ri-store-2-line" className="h-10 w-14 shrink-0 rounded-lg" />
                        <span className="font-medium text-content">{w.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2"><Badge tone={MODE_TONES[m]} size="sm">{L.mode(m)}</Badge></td>
                    <td className="px-3 py-2 text-content-secondary">{w.city || "—"}</td>
                    <td className="px-3 py-2 text-content-secondary">{w.contactName || "—"}<br /><span className="text-[11px] text-content-muted">{w.phone}</span></td>
                    <td className="px-3 py-2 text-right tabular-nums text-content">{s.orders}{s.open ? <span className="ml-1 text-warning">({s.open})</span> : null}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-content">{fmtMoney(s.spend, L.locale)}</td>
                    <td className="px-3 py-2"><Actions w={w} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {form ? (
        <Modal
          title={form.workshop ? `${t("nuvexAccess.edit")} — ${form.workshop.name}` : t("mantenimiento.newWorkshop")}
          onClose={() => setForm(null)}
          themeClasses={tc}
          content={
            <EntityForm
              fields={fields}
              initial={form.workshop ? { ...form.workshop, specialties: (form.workshop.specialties || []).join(", "), bays: form.workshop.bays ?? "" } : undefined}
              themeClasses={tc}
              submitLabel={form.workshop ? t("nuvexAccess.save") : t("nuvexAccess.create")}
              onCancel={() => setForm(null)}
              onSubmit={submit}
            />
          }
        />
      ) : null}
    </PageShell>
  );
}
